import React, { useEffect, useState } from 'react';
import { postRequest, getRequest } from '../UrlRequest';
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import './AllAlert.css';

function AllAlert({ patientId }) {
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsLastPageIndex, setNotificationsLastPageIndex] = useState(0);
  const notificationsPageSize = 10;

  useEffect(() => {
    setNotifications([]);
    setNotificationsPage(1);
    setNotificationsLastPageIndex(0);
    setError(null);

    if (tabValue === 1 && patientId) {
      fetchNotifications(1);
    }
  }, [patientId]);

  const sendNotification = async () => {
    if (!patientId) {
      setError('환자가 선택되지 않았습니다.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const notificationData = {
        title: title,
        content: content,
        receivers: [patientId]
      };

      const response = await postRequest('hearton', '/doctor/notification', notificationData, token);

      if (response) {
        setTitle('');
        setContent('');
        setError(null);
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('알림 전송 실패:', error);
      setError('알림 전송에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccess = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSuccess(false);
  };

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    setError(null);
    if (newValue === 1) {
      setNotificationsPage(1);
      fetchNotifications(1);
    }
  };

  const fetchNotifications = async (page = 1) => {
    if (!patientId) {
      setError('환자를 선택해주세요.');
      setNotifications([]);
      return;
    }

    setNotificationsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await getRequest(
        'hearton',
        `/doctor/notifications/sent/${patientId}?page=${page}&size=${notificationsPageSize}`,
        token
      );
      setNotifications(response.notifications || []);
      setNotificationsLastPageIndex(response.page_count ?? 0);
    } catch (error) {
      console.error('알림 내역 조회 실패:', error);
      setError('알림 내역을 불러오는데 실패했습니다.');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="notifications-container">
      <div className="alert-header">
        <Button
          variant={tabValue === 0 ? 'contained' : 'outlined'}
          onClick={() => handleTabChange(0)}
        >
          알림 전송
        </Button>
        <Button
          variant={tabValue === 1 ? 'contained' : 'outlined'}
          onClick={() => handleTabChange(1)}
          disabled={!patientId}
        >
          알림 내역
        </Button>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {tabValue === 0 && (
        <Box className="alert-form" sx={{ mt: 2 }}>
          <TextField
            className="alert-input"
            label="알림 제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="알림 제목을 입력하세요"
            sx={{ mb: 2 }}
          />
          <TextField
            className="alert-input"
            label="알림 내용"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder="알림 내용을 입력하세요"
            sx={{ mb: 2 }}
          />

          <div className="patient-info">
            <PersonIcon className="patient-icon" />
            <span className="patient-text">
              {patientId ? `환자 ID: ${patientId}` : '환자를 선택해주세요'}
            </span>
          </div>

          <Button
            className="alert-button"
            variant="contained"
            fullWidth
            onClick={sendNotification}
            disabled={!title || !content || !patientId || loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : '알림 전송'}
          </Button>
        </Box>
      )}

      {tabValue === 1 && (
        <Box sx={{ mt: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              페이지: {notificationsPage} / {notificationsLastPageIndex + 1}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const nextPage = Math.max(1, notificationsPage - 1);
                  setNotificationsPage(nextPage);
                  fetchNotifications(nextPage);
                }}
                disabled={notificationsLoading || notificationsPage <= 1}
              >
                이전
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const nextPage = Math.min(notificationsLastPageIndex + 1, notificationsPage + 1);
                  setNotificationsPage(nextPage);
                  fetchNotifications(nextPage);
                }}
                disabled={
                  notificationsLoading ||
                  notificationsPage >= notificationsLastPageIndex + 1
                }
              >
                다음
              </Button>
            </Box>
          </Box>
          {notificationsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : notifications.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                보낸 알림이 없습니다
              </Typography>
            </Paper>
          ) : (
            <List>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id || index}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Typography variant="h6" component="div">
                          {notification.sender || '발신자'}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                            {notification.content}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            전송일시: {formatDate(notification.created_at || notification.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      )}

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        message="알림이 성공적으로 전송되었습니다"
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleCloseSuccess}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
}

export default AllAlert;